class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.classList.toggle('is-empty', parsedState.item_count === 0);
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

if (!customElements.get('cart-drawer-addons')) {
  customElements.define(
    'cart-drawer-addons',
    class CartDrawerAddons extends HTMLElement {
      constructor() {
        super();
        this.addEventListener('click', this.onClick.bind(this));
      }

      async onClick(event) {
        const button = event.target.closest('[data-cart-addon-add], [data-cart-addon-remove]');
        if (!button || button.disabled) return;

        event.preventDefault();
        button.disabled = true;
        button.classList.add('is-loading');
        if (button.classList.contains('cart-drawer-protection__toggle')) {
          button.classList.toggle('is-active', !!button.dataset.cartAddonAdd);
          button.setAttribute('aria-pressed', button.dataset.cartAddonAdd ? 'true' : 'false');
        }

        try {
          let cartData;
          if (button.dataset.cartAddonAdd) {
            cartData = await this.addVariant(button.dataset.cartAddonAdd);
          } else if (button.dataset.cartAddonRemove) {
            cartData = await this.removeLine(button.dataset.cartAddonRemove);
          }

          this.renderDrawer(cartData);
        } catch (error) {
          console.error(error);
          const errors = document.getElementById('CartDrawer-CartErrors');
          if (errors) errors.textContent = window.cartStrings?.error || 'Cart could not be updated.';
        } finally {
          button.disabled = false;
          button.classList.remove('is-loading');
        }
      }

      async addVariant(variantId) {
        const response = await fetch(routes.cart_add_url, {
          ...fetchConfig(),
          body: JSON.stringify({
            id: Number(variantId),
            quantity: 1,
            sections: ['cart-drawer', 'cart-icon-bubble'],
            sections_url: window.location.pathname,
          }),
        });

        if (!response.ok) throw new Error('Add to cart failed');
        return response.json();
      }

      async removeLine(line) {
        const response = await fetch(routes.cart_change_url, {
          ...fetchConfig(),
          body: JSON.stringify({
            line: Number(line),
            quantity: 0,
            sections: ['cart-drawer', 'cart-icon-bubble'],
            sections_url: window.location.pathname,
          }),
        });

        if (!response.ok) throw new Error('Cart change failed');
        return response.json();
      }

      renderDrawer(cartData) {
        const drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.renderContents === 'function') {
          drawer.renderContents(cartData);
        }
      }
    }
  );
}

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
